import { AxiosResponse } from 'axios';
import { IPost, IPostRequest } from '../types/Post';
import { IAggregation } from '../types/Aggregation';
import { IPostRelationships } from '../types/PostRelationships';
import { sdkConfig } from '../config/config-sdk';
import { ITints, ITintsWithSocialsAndPersonalization } from '../types/Tint';
import { IConfig } from '../../url-reader';
import { getUrlParamFilters } from '../../theme/src/utils/get-url-param-filters';

export interface ITintsWithInclusions extends ITints, ITintsWithSocialsAndPersonalization { }

export interface PublicPostBody {
  type: string;
  attributes: {
    author: {
      name: string;
      email?: string;
      username: string;
      image_url: string;
    };
    text: string;
    custom_field: string;
    media_url: string[];
    tags?: string;
  };
}

export interface ReviewBody {
  type: string;
  attributes: {
    type?: string;
    author: {
      name?: string;
      email?: string;
    };
    title: string;
    text: string;
    rating: number;
    media_url?: string[];
    tags?: string;
  };
}

export interface IPostsHttpResponseLinks {
  first: string;
  self: string;
  next?: string;
  prev?: string;
}

export interface IGetPostRequestReturnValue {
  data: IPostRequest;
}

export interface IAggregationsHttpResponse {
  data: IAggregation[];
}

export interface IPostsHttpResponse {
  data: IPost[];
  links: IPostsHttpResponseLinks;
  included?: ReadonlyArray<IPostRelationships>;
}

export interface IQueryParamFilters {
  size?: number;
  q?: string;
  social_feed_id?: number[];
  id?: string[];
  language?: string[];
  published_at?: number;
  sentiment?: Sentiment;
  type?: string;
  tag?: string;
  saved_filter_id?: number;
  product_sku?: string;
  rating?: { [key: string]: string } | string;
  sort?: string;
  media_type?: string;
  collapse?: string;
  replies?: string;
  // source?: string; currently not working
  // status?: string[]; currently not working
  // image_object?: string[]; currently not working
}

type Sentiment = 'neutral' | 'positive' | 'negative';

export interface IQueryParams extends Omit<IQueryParamFilters, 'q' | 'tag'> {
  query?: string;
  tags: string;
}

export interface IGetPostsReturnValue {
  posts: ReadonlyArray<IPost>;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostsHttpResponseLinks;
}

export interface IGetAggregationsReturnValue {
  aggregations: ReadonlyArray<IAggregation>;
}

export interface ISinglePost {
  post: IPost;
  postsRelationships?: ReadonlyArray<IPostRelationships>;
  links: IPostsHttpResponseLinks;
}

export type TintInclude = 'personalizations' | 'social_feeds' | 'default_personalization';

const axios = require('axios');

export default class ApiService {
  private readonly tintSlug: string;

  constructor(tintSlug: string) {
    this.tintSlug = tintSlug;
  }

  public getSinglePost(id: string): Promise<ISinglePost> {
    return this.queryPosts({ id: [id] }).then(response => {
      const {
        data: { data, included, links },
      } = response;

      return {
        post: data[0],
        postsRelationships: included,
        links,
      };
    });
  }

  public getPosts(config: IQueryParamFilters = {}, additionalIncludeParam?: string): Promise<IGetPostsReturnValue> {
    return this.queryPosts(config, additionalIncludeParam).then(response => {
      const {
        data: {
          data,
          links: { next, prev, first, self },
          included,
        },
      } = response;

      return {
        posts: data,
        postsRelationships: included,
        links: {
          next,
          prev,
          first,
          self,
        },
      };
    });
  }

  public getAggregations(config: IConfig): Promise<IGetAggregationsReturnValue> {
    return this.getAggregationsData(config).then(response => {
      const {
        data: { data },
      } = response;

      return {
        aggregations: data,
      };
    });
  }

  public getPage(url: string): Promise<IGetPostsReturnValue> {
    return this.get<IPostsHttpResponse>(url).then(response => {
      const {
        data: {
          data,
          links: { next: nextPage, prev: previousPage, first: firstPage, self },
          included,
        },
      } = response;

      return {
        posts: data,
        postsRelationships: included,
        links: { next: nextPage, prev: previousPage, first: firstPage, self },
      };
    });
  }

  public postReview = (body: ReviewBody) => {
    return this.post(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts`, body);
  };

  public postPublicPost = (body: PublicPostBody) => {
    return this.post(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts`, body);
  };

  public postPostsSummary = (config?: IQueryParamFilters) => {
    const queryParams = config ? `?${this.mapConfigToQueryParams(config)}` : '';
    return this.post(
      `${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts/summaries${queryParams}`,
      {}
    );
  };

  public getPublicPostRequest = (requestId: string): Promise<AxiosResponse<IGetPostRequestReturnValue>> => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts/requests/${requestId}`);
  };

  public getTintBasicData = (includes: readonly TintInclude[]): Promise<AxiosResponse<ITintsWithInclusions>> => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}?include=${includes.join(',')}`);
  };

  public getCustomFont = (teamId: number, fontId: number): any => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/teams/${teamId}/fonts/${fontId}.css`);
  };

  public getPersonalization = (personalizationId: number): any => {
    return this.get(`${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/personalizations/${personalizationId}`);
  };

  private convertDateFrom = (fromValue: number): string => {
    if (fromValue < 0) {
      return `filter[published_at][$gte]=${new Date(Date.now() + fromValue * 1000).toISOString()}&`;
    } else {
      return `filter[published_at][$gte]=${new Date(fromValue * 1000).toISOString()}&`;
    }
  };

  private setQueryParam = (key: string, value: any) => {
    switch (key) {
      case 'sort':
      case 'collapse':
        return `${key}=${value}&`;
      case 'size':
        return `page[size]=${value}&`;
      case 'published_at':
        return this.convertDateFrom(value);
      case 'rating':
        return value.hasOwnProperty('range') ? `filter[${key}][$range]=${value.range}&` : `filter[${key}]=${value}&`;
      default:
        return `filter[${key}]=${value}&`;
    }
  };

  private mapConfigToQueryParams = (config: IQueryParamFilters): string =>
    Object.keys(config).reduce((acc, curr) => {
      if (config[curr] !== undefined) {
        const value = Array.isArray(config[curr]) ? config[curr].join(',') : config[curr];
        const nextParam = this.setQueryParam(curr, value);

        return `${acc}${nextParam}`;
      }
      return `${acc}`;
    }, '');

  private queryPosts = (
    config: IQueryParamFilters,
    additionalIncludeParams: string = ''
  ): Promise<AxiosResponse<IPostsHttpResponse>> => {
    return this.get(
      `${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts?${this.mapConfigToQueryParams(
        config
      )}include=product_tags.product,cta_associations.cta${additionalIncludeParams}&`
    );
  };

  private getAggregationsData = (config: IConfig): Promise<AxiosResponse<IAggregationsHttpResponse>> => {
    const _config = this.mapConfigToQueryParams(getUrlParamFilters(config));
    const params = _config?.substring(0, _config?.length - 1);

    const filterParams = params ? `?${params}&filter[media_type]=image` : '?filter[media_type]=image';

    return this.get(
      `${sdkConfig.REACT_APP_TINT_UP_URL_TINTS}/v2/tints/${this.tintSlug}/posts/aggregations${filterParams}&include=product_tags.product,cta_associations.cta&`
    );
  };

  private get = <T, R = AxiosResponse<T>>(url: string, requestId?: string): Promise<R> => {
    return axios.get(url, { requestId });
  };

  private post = <T, R = AxiosResponse<T>>(url: string, payload: T): Promise<R> => {
    return axios.post(url, { data: payload }, { headers: { 'Content-Type': 'application/vnd.api+json' } });
  };
}
